import { useEffect, useMemo, useRef, useState } from "react";
import PhotoTile from "./PhotoTile";

// Column-based masonry that preserves aspect ratios (no cropping).
export default function MasonryGrid({
  photos,
  onOpen,
  loading,
  hasMore,
  onLoadMore,
  emptyState = null,
  testId = "masonry-grid",
}) {
  const sentinelRef = useRef(null);

  const [columns, setColumns] = useState(() => {
    if (typeof window === "undefined") return 4;
    const w = window.innerWidth;
    if (w >= 1280) return 5;
    if (w >= 1024) return 4;
    if (w >= 768) return 3;
    return 2;
  });

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      const next = w >= 1280 ? 5 : w >= 1024 ? 4 : w >= 768 ? 3 : 2;
      setColumns((prev) => (prev === next ? prev : next));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Distribute photos into columns balanced by cumulative height ratio
  const cols = useMemo(() => {
    const buckets = Array.from({ length: columns }, () => []);
    const heights = new Array(columns).fill(0);
    for (const p of photos) {
      const ratio = p.height && p.width ? p.height / p.width : 1;
      let idx = 0;
      for (let i = 1; i < columns; i++) if (heights[i] < heights[idx]) idx = i;
      buckets[idx].push(p);
      heights[idx] += ratio;
    }
    return buckets;
  }, [photos, columns]);

  useEffect(() => {
    if (!onLoadMore || !hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !loading) onLoadMore();
        }
      },
      { rootMargin: "600px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [onLoadMore, hasMore, loading]);

  if (!loading && photos.length === 0 && emptyState) return emptyState;

  return (
    <div data-testid={testId}>
      <div className="flex gap-2">
        {cols.map((col, ci) => (
          <div key={ci} className="flex-1 min-w-0 flex flex-col gap-2">
            {col.map((p) => (
              <PhotoTile key={p.id} photo={p} onOpen={onOpen} />
            ))}
          </div>
        ))}
      </div>

      {loading && <SkeletonRows columns={columns} />}

      {hasMore && <div ref={sentinelRef} data-testid="load-more-sentinel" className="h-6" />}
      {!hasMore && photos.length > 0 && (
        <div className="text-center text-xs uppercase tracking-[0.3em] text-muted-foreground py-10">
          — end of results —
        </div>
      )}
    </div>
  );
}

function SkeletonRows({ columns }) {
  const RATIOS = [1.2, 0.7, 1.5, 0.9, 1.3, 0.8];
  return (
    <div className="flex gap-2 mt-2" data-testid="skeleton-rows">
      {Array.from({ length: columns }).map((_, ci) => (
        <div key={ci} className="flex-1 flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, ri) => (
            <div
              key={ri}
              className="w-full es-shimmer"
              style={{ aspectRatio: `${1 / RATIOS[(ci + ri) % RATIOS.length]}` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
